import { apiClient } from './client'

export interface Vehicle {
  id: string
  institutionId: string
  vehicleNumber: string
  make: string
  vehicleModel: string
  year: number
  capacity: number
  fuelType: 'petrol' | 'diesel' | 'electric' | 'hybrid'
  vehicleType: 'bus' | 'van' | 'car' | 'minibus'
  color: string
  imei?: string
  engineNumber?: string
  chassisNumber?: string
  registrationDate: string
  insuranceNumber?: string
  insuranceExpiry?: string
  roadworthinessExpiry?: string
  assignedDriverId?: string
  assignedRouteIds?: string[]
  currentLocation?: {
    latitude: number
    longitude: number
    address?: string
    lastUpdated: string
  }
  status: 'active' | 'maintenance' | 'retired' | 'out_of_service'
  isTracking: boolean
  features: {
    gps: boolean
    rfid: boolean
    camera: boolean
    airConditioning: boolean
    wheelchair: boolean
    firstAid: boolean
  }
  maintenance?: {
    lastServiceDate?: string
    nextServiceDate?: string
    mileage?: number
    serviceHistory?: {
      date: string
      type: string
      description: string
      cost?: number
      serviceProvider?: string
    }[]
  }
  documents?: {
    type: 'registration' | 'insurance' | 'roadworthiness' | 'permit'
    number: string
    issuedDate: string
    expiryDate: string
    documentUrl?: string
  }[]
  isActive: boolean
  createdById: string
  createdAt: string
  updatedAt: string
  // Populated/Enhanced fields
  assignedDriver?: {
    id: string
    firstName: string
    lastName: string
    email: string
    phone: string
  }
  currentTrip?: {
    id: string
    status: string
    tripType: string
    tripDate: string
  }
  assignedRoutes?: {
    id: string
    name: string
    number: string
    description?: string
  }[]
  recentTrips?: {
    id: string
    tripDate: string
    tripType: string
    status: string
    route?: any
    driver?: any
  }[]
  maintenanceDue?: boolean
  documentsExpiringSoon?: boolean
}

export interface CreateVehicleRequest {
  vehicleNumber: string
  make: string
  vehicleModel: string
  year: number
  capacity: number
  fuelType: 'petrol' | 'diesel' | 'electric' | 'hybrid'
  vehicleType: 'bus' | 'van' | 'car' | 'minibus'
  color: string
  imei: string
  engineNumber?: string
  chassisNumber?: string
  registrationDate: string
  assignedDriverId?: string
  institutionId?: string // For super admins
}

export interface UpdateVehicleRequest {
  vehicleNumber?: string
  make?: string
  vehicleModel?: string
  year?: number
  capacity?: number
  fuelType?: 'petrol' | 'diesel' | 'electric' | 'hybrid'
  vehicleType?: 'bus' | 'van' | 'car' | 'minibus'
  color?: string
  imei?: string
  engineNumber?: string
  chassisNumber?: string
  assignedDriverId?: string
  status?: 'active' | 'maintenance' | 'retired' | 'out_of_service'
  isTracking?: boolean
  features?: {
    gps?: boolean
    rfid?: boolean
    camera?: boolean
    airConditioning?: boolean
    wheelchair?: boolean
    firstAid?: boolean
  }
  maintenance?: {
    lastServiceDate?: string
    nextServiceDate?: string
    mileage?: number
  }
  currentLocation?: {
    latitude: number
    longitude: number
    address?: string
  }
}

export interface GetVehiclesParams {
  page?: number
  limit?: number
  search?: string
  status?: string
  vehicleType?: string
  assignedDriverId?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  institutionId?: string // For super admins
}

export interface FleetStats {
  totalVehicles: number
  activeTrips: number
  maintenanceDue: number
  documentsExpiring: number
  activeVehicles: number
  maintenanceVehicles: number
  retiredVehicles: number
  outOfServiceVehicles: number
  buses: number
  vans: number
  cars: number
  minibuses: number
}

export interface VehicleTracking {
  id: string
  vehicleNumber: string
  make: string
  vehicleModel: string
  status: string
  currentLocation?: {
    latitude: number
    longitude: number
    address?: string
    lastUpdated: string
  }
  driver?: {
    id: string
    firstName: string
    lastName: string
    phone: string
  }
  currentTrip?: {
    id: string
    routeName: string
    tripType: string
    studentCount: number
  }
}

// Get all vehicles with optional filtering and pagination
export const getVehicles = async (params: GetVehiclesParams = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/fleet?${searchParams.toString()}`)
  return response.data
}

// Get single vehicle by ID
export const getVehicle = async (id: string) => {
  const response = await apiClient.get(`/fleet/${id}`)
  return response.data
}

// Create new vehicle
export const createVehicle = async (vehicleData: CreateVehicleRequest) => {
  const response = await apiClient.post('/fleet', vehicleData)
  return response.data
}

// Update existing vehicle
export const updateVehicle = async (id: string, vehicleData: UpdateVehicleRequest) => {
  const response = await apiClient.put(`/fleet/${id}`, vehicleData)
  return response.data
}

// Delete vehicle (soft delete)
export const deleteVehicle = async (id: string) => {
  const response = await apiClient.delete(`/fleet/${id}`)
  return response.data
}

// Get fleet statistics
export const getFleetStats = async (institutionId?: string) => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/fleet/stats${params}`)
  return response.data
}

// Get vehicle tracking data
export const getVehicleTracking = async (institutionId?: string) => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/fleet/tracking${params}`)
  return response.data
}

// Quick vehicle status update
export const updateVehicleStatus = async (id: string, status: Vehicle['status']) => {
  return updateVehicle(id, { status })
}

// Toggle vehicle tracking
export const toggleVehicleTracking = async (id: string, isTracking: boolean) => {
  return updateVehicle(id, { isTracking })
}

// Update vehicle location (for GPS tracking)
export const updateVehicleLocation = async (id: string, location: {
  latitude: number
  longitude: number
  address?: string
}) => {
  return updateVehicle(id, { currentLocation: location })
}